import type { AwardLetterLine, AwardLetters } from '@myrewards/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { letterKeys, savePublicCitation } from '../../api/letters';
import { problemText } from '../../settings/parts';
import { Button, ConfirmDialog, TextField } from '../../ui';
import s from '../Steps.module.css';
import o from '../Ops.module.css';
import { wordingHint } from './presenter';

/** Public wording is entered deliberately; confidential justification is never copied here. */
export function PublicCitation({
  letters,
  line,
  version,
}: {
  letters: AwardLetters;
  line: AwardLetterLine;
  version: number;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [citation, setCitation] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (line.lineUpdatedAt === undefined) return null;
  return (
    <div className={o.stack}>
      <p className={o.muted}>{line.publicCitation ?? 'No public award wording entered yet.'}</p>
      {editing ? (
        <>
          <TextField
            label={`Public wording for ${line.person.name}`}
            multiline
            value={citation}
            onChange={setCitation}
            maxLength={300}
            isRequired
            description={`Printed in the recipient’s letter. ${wordingHint(letters.type, line.selectedTemplate?.notes)} Use only wording approved for the recipient; confidential justification stays separate.`}
          />
          <div className={s.generateRow}>
            <Button
              variant="ghost"
              isDisabled={citation.trim() === '' || citation.trim() === line.publicCitation}
              onPress={() => {
                setConfirming(true);
              }}
            >
              Review wording
            </Button>
            <Button
              variant="ghost"
              onPress={() => {
                setEditing(false);
              }}
            >
              Cancel
            </Button>
          </div>
        </>
      ) : (
        <Button
          variant="ghost"
          size="sm"
          aria-label={`Edit public wording for ${line.person.name}`}
          onPress={() => {
            setCitation(line.publicCitation ?? '');
            setEditing(true);
            setError(null);
          }}
        >
          Edit public wording
        </Button>
      )}
      <ConfirmDialog
        isOpen={confirming}
        onOpenChange={setConfirming}
        title="Save public award wording?"
        intro="This text will appear in the letter. Generate it again after saving, then review the new PDF."
        summary={[
          { label: 'Recipient', value: line.person.name },
          { label: 'Public wording', value: citation.trim() },
        ]}
        confirmLabel="Save wording"
        error={error}
        onConfirm={async (reason) => {
          if (line.lineUpdatedAt === undefined) return false;
          try {
            const next = await savePublicCitation(letters.requestId, line.lineId, {
              version,
              lineUpdatedAt: line.lineUpdatedAt,
              publicCitation: citation.trim(),
              reason,
            });
            queryClient.setQueryData(letterKeys.one(letters.requestId), next);
            setEditing(false);
            return true;
          } catch (err) {
            setError(problemText(err));
            return false;
          }
        }}
      />
    </div>
  );
}
